import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { FilterSection } from '../../../../components/common/filter/FilterSection/FilterSection';
import { FilterShortcutButtonSection } from '../../../../components/common/filter/FilterShortcutButtonSection/FilterShortcutButtonSection';
import { NoFilteredItem } from '../../../../components/common/filter/NoFilteredItem';
import { MainHead } from '../../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../../components/common/loading/AnimateLoading';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { userOwnTractorFilterShortcutInfos } from '../../../../constants/filter/shortCut/filterShortcut.constant';
import { IUserOwnTractorFilter } from '../../../../constants/filter/userOwnTractorFilter.constant';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { usePatchFlarelaneUserTags } from '../../../../hooks/api/flarelane/usePatchFlarelaneUserTags';
import { useDeletePokeToUserOwnTractor } from '../../../../hooks/api/product/mutations/useDeletePokeToUserOwnTractor.mutation';
import { usePatchUserOwnTractorViewsById } from '../../../../hooks/api/product/mutations/usePatchUserOwnTractorViewsById.mutation';
import { usePostPokeToUserOwnTractor } from '../../../../hooks/api/product/mutations/usePostPokeToUserOwnTractor.mutation';
import { useGetNewUsersOwnProductQuery } from '../../../../hooks/api/product/queries/useGetNewUsersOwnProduct.query';
import { useGetUsersOwnTractors } from '../../../../hooks/api/product/queries/useGetUsersOwnTractors.query';
import { usePostUserActivityTracking } from '../../../../hooks/api/user/mutations/usePostUserActivityTracking.mutation';
import { useAddFilterOptions } from '../../../../hooks/filter/useAddFilterOptions';
import { useCheckSearchParamUpdate } from '../../../../hooks/filter/useCheckSearchParamUpdate';
import { useUpdatePokingListPageSearchParam } from '../../../../hooks/filter/useUpdatePokingListPageSearchParam';
import { useUpdateUserOwnTractorFilterStates } from '../../../../hooks/filter/useUpdateUserOwnTractorFilterStates';
import { useGetDownloadAppModalHeight } from '../../../../hooks/modal/useGetDownloadAppModalHeight';
import { useMarkNotificationAsRead } from '../../../../hooks/page/useMarkNotificationAsRead';
import { useLayout } from '../../../../hooks/screen/useLayout';
import { useInfiniteScrollLoadingTarget } from '../../../../hooks/scroll/useInfiniteScrollLoadingTarget';
import { useAppSelector } from '../../../../hooks/store/useStore';
import { useAdvertisementConsent } from '../../../../hooks/user/useAdvertisementConsent';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { IMachine } from '../../../../interfaces/Machine.interfaces';
import { IUserOwnTractorListItem } from '../../../../interfaces/OwnTractor.interfaces';
import { createUserOwnTractorFilterSearchParams } from '../../../../utils/filter/createUserOwnTractorFilterSearchParams.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { UserOwnProductDetailCard } from '../../../../views/Product/listView/UserOwnProductDetailCard/UserOwnProductDetailCard';
import {
  UserOwnProductDetailCardWrapper,
  UserOwnProductListDetailCardGroup,
} from '../../../../views/Product/listView/UserOwnProductDetailCard/UserOwnProductListDetailCardGroup';
import { MarketAlarmButton } from '../../../Product/ProductListPage/components/MarketAlarmButton';
import { OpenMarketCarouselBanners } from '../../components/OpenMarketCarouselBanners';
import { OpenMarketListWrapper } from '../../components/OpenMarketListWrapper';

export const TractorPokingListPage = () => {
  useAddFilterOptions({ filterType: 'userOwnTractorFilter', data: 'brands', applicableMachineType: 'tractor' });
  useCheckSearchParamUpdate({ filterType: 'userOwnTractorFilter' });
  useUpdatePokingListPageSearchParam();
  useUpdateUserOwnTractorFilterStates();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isMyTractorModalOpen, setIsMyTractorModalOpen] = useState(false);
  const [isPokeCancelSuccessModalOpen, setIsPokeCancelSuccessModalOpen] = useState(false);
  const [isPokeSuccessModalOpen, setIsPokeSuccessModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isAdvertisementConsentModalOpen, setIsAdvertisementConsentModalOpen] = useState(false);
  const [selectedUserOwnProduct, setSelectedUserOwnProduct] = useState<IUserOwnTractorListItem>();
  const [isUserActivityUpdated, setIsUserActivityUpdated] = useState<boolean>(false);

  const { userProfile } = useGetUserProfile();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const hasAgreedToAdvertisementTerms = userProfile?.advertisementTermsAgreement;
  const fixedHeaderHeight = hasAgreedToAdvertisementTerms ? 48 : 86;
  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  const userOwnTractorsFilterStates = useAppSelector((state) => state.filter.userOwnTractorFilter);

  const updateAdConsent = useAdvertisementConsent('TLP');
  const { mutate: postPokeToUserOwnTractor } = usePostPokeToUserOwnTractor();
  const { mutate: deletePokeToUserOwnTractor } = useDeletePokeToUserOwnTractor();
  const { mutate: postUserActivityTracking } = usePostUserActivityTracking();
  const { mutate: patchFlarelaneUserTags } = usePatchFlarelaneUserTags();

  const { data: newUsersOwnProduct } = useGetNewUsersOwnProductQuery();
  useMarkNotificationAsRead(newUsersOwnProduct, 'newUsersOwnProductInfo');

  const {
    data: userOwnProducts,
    isLoading: isGetUsersOwnTractorsQueryLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchPokingList,
  } = useGetUsersOwnTractors({
    searchParam: {
      ...createUserOwnTractorFilterSearchParams({
        filters: userOwnTractorsFilterStates.globalFilters as IUserOwnTractorFilter[],
      }),
      count: 5,
    },
  });

  const { loadingTargetRef, isLoadingTargetRendered } = useInfiniteScrollLoadingTarget({
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    triggeredFnWhenInView: fetchNextPage,
  });

  const { mutate: patchUserOwnTractorViewsById } = usePatchUserOwnTractorViewsById();

  const handleShowDetail = (userOwnProduct: IUserOwnTractorListItem) => {
    ampli.pvmClickTractorPokingCard({
      title: getOpenMarketProductTitle({
        brandName: userOwnProduct.brand?.name,
        machine: userOwnProduct.machine as IMachine,
      }),
    });
    patchUserOwnTractorViewsById(
      {
        productId: userOwnProduct.id,
        reqBody: {
          views: userOwnProduct.views + 1,
        },
      },
      {
        onSuccess: () => {
          navigate(`${pageInfo.tractorPokingDetailPage.path}/${userOwnProduct.id}`);
        },
      },
    );
  };

  const handlePokeClick = (userOwnProduct: IUserOwnTractorListItem) => {
    if (!userOwnProduct || !userOwnProduct.id) {
      return;
    }

    setSelectedUserOwnProduct(userOwnProduct);

    // TODO: 추가된 속성값 구현
    const pokingCardTitle = getOpenMarketProductTitle({
      brandName: userOwnProduct.brand?.name,
      machine: userOwnProduct.machine as IMachine,
    });
    ampli.pvmClickPoke({
      title: pokingCardTitle,
      type: 'quick',
      price: 0,
    });

    if (!isUserLoggedIn) {
      setIsLoginModalOpen(true);
      return;
    }

    if (!hasAgreedToAdvertisementTerms) {
      setIsAdvertisementConsentModalOpen(true);
      return;
    }

    executePoke(userOwnProduct);
  };

  const handleSavePageAndRedirectToLogin = () => {
    setIsLoginModalOpen(false);
    navigate(pageInfo.loginPage.path);
  };

  const handlePokeWithAgreement = (userOwnProduct: IUserOwnTractorListItem) => {
    updateAdConsent({
      isAgreeing: true,
      onSuccess: () => {
        setIsAdvertisementConsentModalOpen(false);
        executePoke(userOwnProduct);
      },
    });
  };

  const executePoke = (userOwnProduct: IUserOwnTractorListItem) => {
    if (!userOwnProduct) {
      return;
    }

    postPokeToUserOwnTractor(
      {
        tractorId: userOwnProduct.id,
        reqBody: {
          suggestedPrice: null,
        },
      },
      {
        onSuccess: () => {
          setIsPokeSuccessModalOpen(true);

          // TODO: 추가된 속성값 구현
          const pokingCardTitle = getOpenMarketProductTitle({
            brandName: userOwnProduct.brand?.name,
            machine: userOwnProduct.machine as IMachine,
          });
          ampli.pvmClickPokeCompleted({
            user_id: userProfile?.id || '',
            title: pokingCardTitle,
            ps: userOwnProduct.machine?.engineOutputInPs,
            manufacturedYear: userOwnProduct.manufacturedYear,
            operationTime: userOwnProduct.operationTime,
            type: 'quick',
            price: 0,
          });
          ReactGA.event({
            category: 'poke',
            action: 'completed',
            label: pokingCardTitle,
            value: 0,
          });
        },
      },
    );
  };

  const handlePokeCancelClick = (userOwnProduct: IUserOwnTractorListItem) => {
    if (!userOwnProduct || !userOwnProduct.id) {
      return;
    }

    deletePokeToUserOwnTractor(
      {
        tractorId: userOwnProduct.id,
      },
      {
        onSuccess: () => {
          setIsPokeCancelSuccessModalOpen(true);
        },
      },
    );
  };

  const getUserOwnProductStatusIconName = (userOwnProduct: IUserOwnTractorListItem) => {
    if (userOwnProduct.isMyTractor) {
      return 'myTractor';
    }

    if (!userOwnProduct.isMyTractor && userOwnProduct.isTractorPokedByMe) {
      return 'poked';
    }

    return 'pokable';
  };

  const handleStatusIconClick = (userOwnProduct: IUserOwnTractorListItem) => {
    const statusIconName = getUserOwnProductStatusIconName(userOwnProduct);

    switch (statusIconName) {
      case 'myTractor':
        setIsMyTractorModalOpen(true);
        return;
      case 'poked':
        handlePokeCancelClick(userOwnProduct);
        return;
      default:
        handlePokeClick(userOwnProduct);
    }
  };

  useEffect(() => {
    if (!isUserActivityUpdated) {
      postUserActivityTracking('pokeListPageVisitedCount', {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['user_activity_tracking', 'TLP'] });
          setIsUserActivityUpdated(true);
        },
      });
    }
  }, [postUserActivityTracking, isUserActivityUpdated, queryClient]);

  useEffect(() => {
    if (userProfile) {
      patchFlarelaneUserTags({ lastVisitedTLP: new Date().toISOString() });
    }
  }, [userProfile, patchFlarelaneUserTags]);

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: false }} headerTitle={'찔러보기'} hasMyInfoButton />
        <MarketAlarmButton pageType={'TLP'} />
      </>
    ),
    fixedHeaderHeight,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
    style: { background: '#fff' },
  });

  if (isGetUsersOwnTractorsQueryLoading) {
    return <AnimateLoading />;
  }

  if (userOwnProducts) {
    return (
      <>
        <Helmet>
          <title>{pageInfo.tractorPokingListPage.title}</title>
        </Helmet>

        <OpenMarketListWrapper>
          <>
            <FilterSection
              filterType={'userOwnTractorFilter'}
              style={{
                position: 'sticky',
                top: `${fixedHeaderHeight + downloadAppModalHeight}px `,
                backgroundColor: 'white',
                zIndex: 55,
              }}
            />
            <OpenMarketCarouselBanners category={'CAROUSEL_MAIN'} outputLocation={'tractor-poking'} />
            <FilterShortcutButtonSection filterShortcutConstant={userOwnTractorFilterShortcutInfos} />
            <UserOwnProductListDetailCardGroup>
              <PokingInfoSection>
                <PokingInfoTitle>{'🌟 찔러보기가 뭐예요?'}</PokingInfoTitle>
                <PokingInfoContent>
                  {'다른 회원이 보유하고 있는 트랙터를 구경하고 판매 요청을 할 수 있는 기능이예요!'}
                </PokingInfoContent>
              </PokingInfoSection>

              {!isGetUsersOwnTractorsQueryLoading && userOwnProducts && userOwnProducts.pages[0].length === 0 && (
                <NoFilteredItem filterType={'userOwnTractorFilter'} />
              )}

              {userOwnProducts && (
                <>
                  {userOwnProducts.pages.map((page) => {
                    return page.map((userOwnProduct) => {
                      return (
                        <UserOwnProductDetailCardWrapper key={userOwnProduct.id}>
                          <UserOwnProductDetailCard
                            handleClick={() => handleShowDetail(userOwnProduct)}
                            userOwnProduct={userOwnProduct}
                            isNewBadgeVisible
                            statusIconName={getUserOwnProductStatusIconName(userOwnProduct)}
                            statusIconClickHandler={() => handleStatusIconClick(userOwnProduct)}
                          />
                        </UserOwnProductDetailCardWrapper>
                      );
                    });
                  })}
                  {isLoadingTargetRendered && <LoadingTarget ref={loadingTargetRef} />}
                </>
              )}
            </UserOwnProductListDetailCardGroup>
          </>
        </OpenMarketListWrapper>

        <PopupConfirmModal
          isShow={isPokeCancelSuccessModalOpen}
          hasCloseButton={false}
          title={'🙂 취소가 완료 되었어요'}
          description={'요청하신 찔러보기가 취소되었어요'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => {
                refetchPokingList();
                setIsPokeCancelSuccessModalOpen(false);
              },
              color: 'blue',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isMyTractorModalOpen}
          hasCloseButton={false}
          title={'🙋🏻‍♂ ️내가 보유한 트랙터예요!'}
          description={'다른 트랙터 구경하고\n콕! 콕! 찔러보기 해보세요!'}
          buttonArray={[
            {
              text: '확인',
              onClick: () => setIsMyTractorModalOpen(false),
              color: 'blue',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isPokeSuccessModalOpen}
          hasCloseButton={false}
          title={'👌🏻 요청이 완료되었어요!'}
          description={'찔러보기 하신 트랙터가 판매중으로\n전환되면 알람을 보내드릴게요!'}
          buttonArray={[
            {
              text: '찔러본 트랙터 보러가기',
              onClick: () => {
                setIsPokeSuccessModalOpen(false);
                navigate(pageInfo.myInfoPokeListPage.path);
              },
              color: 'blue',
            },
            {
              text: '돌아가기',
              onClick: () => {
                refetchPokingList();
                setIsPokeSuccessModalOpen(false);
              },
              color: 'gray',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isAdvertisementConsentModalOpen}
          hasCloseButton={false}
          closeModal={() => setIsAdvertisementConsentModalOpen(false)}
          title={'🔔 알람 동의를 해주세요!'}
          description={'찔러보기 하신 매물의 판매 알람을\n받으시려면 알람 동의가 필요해요!'}
          buttonArray={[
            {
              text: '동의하고 알람 받기',
              onClick: () => selectedUserOwnProduct && handlePokeWithAgreement(selectedUserOwnProduct),
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: () => {
                ampli.pvmClickSetAlarmModalClose({
                  page_type: 'TLP',
                });
                setIsAdvertisementConsentModalOpen(false);
              },
              color: 'gray',
            },
          ]}
        />

        <PopupConfirmModal
          isShow={isLoginModalOpen}
          hasCloseButton={false}
          closeModal={() => setIsLoginModalOpen(false)}
          title={'로그인이 필요합니다.'}
          description={'로그인하러 가시겠습니까?'}
          buttonArray={[
            {
              text: '로그인',
              onClick: handleSavePageAndRedirectToLogin,
              color: 'blue',
            },
            {
              text: '닫기',
              onClick: () => setIsLoginModalOpen(false),
              color: 'gray',
            },
          ]}
        />
      </>
    );
  }
  return null;
};

const PokingInfoSection = styled.div`
  display: flex;
  padding: 24px 30px;
  flex-direction: column;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PokingInfoTitle = styled.h3`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);

  /* Body/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const PokingInfoContent = styled.p`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const LoadingTarget = styled.div``;
